import { useState, useEffect, useCallback } from 'react'

import { useSearchParams } from 'react-router-dom'

const STORAGE_PREFIX = 'bt_welcome_modal_'
const SHOW_GUIDE_PARAM = 'showGuide'

export type WelcomeModalKey = 'groups' | 'music' | 'schedules' | 'campaigns'

interface UseWelcomeModalOptions {
	/**
	 * Unique key to identify the modal in localStorage
	 */
	modalKey: WelcomeModalKey
}

interface UseWelcomeModalReturn {
	/**
	 * Whether the modal should be visible
	 */
	showModal: boolean
	/**
	 * Handler to close the modal and save to localStorage
	 */
	handleCloseModal: () => void
}

/**
 * Hook to manage welcome modal visibility with localStorage persistence
 * and URL parameter override for guide navigation.
 *
 * The modal will show when:
 * 1. URL contains ?showGuide=true parameter (forces modal to show), OR
 * 2. User hasn't seen this modal before (not in localStorage)
 *
 * When user clicks continue:
 * - Modal closes
 * - Preference is saved to localStorage
 * - showGuide parameter is removed from URL
 */
export function useWelcomeModal({
	modalKey,
}: UseWelcomeModalOptions): UseWelcomeModalReturn {
	const [searchParams, setSearchParams] = useSearchParams()

	const [showModal, setShowModal] = useState(false)
	const [hasInitialized, setHasInitialized] = useState(false)

	const storageKey = `${STORAGE_PREFIX}${modalKey}_seen`
	const forceShow = searchParams.get(SHOW_GUIDE_PARAM) === 'true'

	// Check if modal has been seen before
	const hasSeenModal = useCallback(() => {
		try {
			return localStorage.getItem(storageKey) === 'true'
		} catch {
			// localStorage might not be available
			return false
		}
	}, [storageKey])

	// Save that modal has been seen
	const markModalAsSeen = useCallback(() => {
		try {
			localStorage.setItem(storageKey, 'true')
		} catch {
			// localStorage might not be available
		}
	}, [storageKey])

	// Remove showGuide parameter from URL
	const removeShowGuideParam = useCallback(() => {
		if (searchParams.has(SHOW_GUIDE_PARAM)) {
			const newParams = new URLSearchParams(searchParams)
			newParams.delete(SHOW_GUIDE_PARAM)
			setSearchParams(newParams, { replace: true })
		}
	}, [searchParams, setSearchParams])

	// Show welcome modal when:
	// 1. forceShow is true (from guide navigation with showGuide=true), OR
	// 2. User hasn't seen this modal before
	useEffect(() => {
		if (!hasInitialized) {
			if (forceShow || !hasSeenModal()) {
				setShowModal(true)
			}
			setHasInitialized(true)
		}
	}, [hasInitialized, forceShow, hasSeenModal])

	// Handler to close the modal
	const handleCloseModal = useCallback(() => {
		setShowModal(false)
		markModalAsSeen()
		removeShowGuideParam()
	}, [markModalAsSeen, removeShowGuideParam])

	return {
		showModal,
		handleCloseModal,
	}
}
